import React, { useContext, useState } from "react";
import styles from "./mortgage-view.module.scss";
import * as yup from "yup";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useQuery } from "../../hooks/useQuery";
import { useHistory } from "react-router";
import ToastContext from "../../contexts/ToastContext";
import LoadingContext from "../../contexts/LoadingContext";
import { DocumentTypes, UploadDocMortSalaryBusinessReq } from "../../types/mortgage";
import {
  mortgageDocumentUploadApi,
  submitMortgageApplication,
} from "../../swr/mortgage";
import ModalLayout from "../../components/ModalLayout";
import SuccessModal from "../../components/Modal/success-modal";
import InputWithHeader from "../../components/Input/InputWithHeader";
import FileUploadInput from "../../components/Input/file-upload-input";
import PopUp from "../../components/Insurance/PopUp";

const schema = yup.object({
  hasAccessAccount: yup.boolean().required("Please select an option"),

  accountNumber: yup
    .string()
    .defined()
    .when("hasAccessAccount", {
      is: true,
      then: (schema) => schema.min(1).required("Account number is required"),
      otherwise: (schema) => schema.default(""),
    }),

  // ── Always required ──
  statementOfAccount: yup
    .mixed<File>()
    .required("Statement of Bank Account is required")
    .test("fileSize", "File must be 10MB or less", (file) =>
      file ? file.size / (1024 * 1024) <= 10 : false
    ),

  meansOfIdentification: yup
    .mixed<File>()
    .required("Means of Identification is required")
    .test("fileSize", "File must be 10MB or less", (file) =>
      file ? file.size / (1024 * 1024) <= 10 : false
    ),

  letterOfEmployment: yup
  .mixed<File>()
  .when("$isBusinessApplicant", {
    is: false,
    then: (schema) =>
      schema
        .required("Letter of Employment is required")
        .test("fileSize", "File must be 10MB or less", (file) =>
          file ? file.size / (1024 * 1024) <= 10 : false
        ),
    otherwise: (schema) => schema.notRequired().default(undefined),
  }) as yup.MixedSchema<File | undefined>,

paySlip: yup
  .mixed<File>()
  .when("$isBusinessApplicant", {
    is: false,
    then: (schema) =>
      schema
        .required("Last 6 months Payslip is required")
        .test("fileSize", "File must be 10MB or less", (file) =>
          file ? file.size / (1024 * 1024) <= 10 : false
        ),
    otherwise: (schema) => schema.notRequired().default(undefined),
  }) as yup.MixedSchema<File | undefined>,

evidenceOfPayment: yup
  .mixed<File>()
  .when("$isBusinessApplicant", {
    is: false,
    then: (schema) =>
      schema
        .required("Evidence of Payment is required")
        .test("fileSize", "File must be 10MB or less", (file) =>
          file ? file.size / (1024 * 1024) <= 10 : false
        ),
    otherwise: (schema) => schema.notRequired().default(undefined),
  }) as yup.MixedSchema<File | undefined>,

businessRegistration: yup
  .mixed<File>()
  .when("$isBusinessApplicant", {
    is: true,
    then: (schema) =>
      schema
        .required("Business Registration Document is required")
        .test("fileSize", "File must be 10MB or less", (file) =>
          file ? file.size / (1024 * 1024) <= 10 : false
        ),
    otherwise: (schema) => schema.notRequired().default(undefined),
  }) as yup.MixedSchema<File | undefined>,
});

const IntMortgageApp = () => {
  let query = useQuery();
  const history = useHistory();

  const { openError } = useContext(ToastContext);
  const { setLoading } = useContext(LoadingContext);

  const [submitSuccessful, setSubmitSuccessful] = useState(false);
  const isBusinessApplicant =
    query.get("epytppa") && query.get("epytppa") === "business" ? true : false;

  const [dropDownStates, setDropDownStates] = useState({
    hasAccessAccount: false,
  });

  const toggleDropdown = (key: keyof typeof dropDownStates, open: boolean) => {
    setDropDownStates((prev) => ({ ...prev, [key]: open }));
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm({
    resolver: yupResolver(schema, { context: { isBusinessApplicant } }),
    defaultValues: {
      hasAccessAccount: undefined,
      letterOfEmployment: undefined,
      paySlip: undefined,
      evidenceOfPayment: undefined,
      statementOfAccount: undefined,
      meansOfIdentification: undefined,
      accountNumber: undefined,
    },
  });

  const hasAccessAccount = watch("hasAccessAccount");

  const onSubmit = async (req: UploadDocMortSalaryBusinessReq) => {
    setLoading(true);
    try { 
      const appId = query.get("dippa");
      const { hasAccessAccount, accountNumber, ...rest } = req;

      const entries = (Object.entries(rest) as [string, File][]).filter(
        ([, file]) => file != null
      );
      await mortgageDocumentUploadApi({
        MortgageApplicationId: appId ? Number(appId) : 0,
        DocumentTypes: entries.map(
          ([doc]) => DocumentTypes.find((type) => type.finder === doc)?.value ?? 0
        ),
        Files: entries.map(([, file]) => file),
      });

      const { data, error } = await submitMortgageApplication(
        appId ? Number(appId) : 0
      );

      if (data) {
        setSubmitSuccessful(true);
      }

      if (error) {
        openError(
          error?.response?.data?.Message ||
            error?.response?.data?.message ||
            "An error occurred"
        );
      }
    } catch (error: any) {
      openError(
        error?.response?.data?.Message ||
          error?.response?.data?.message ||
          "An error occurred"
      );
    } finally {
      setLoading(false);
    }
  };

  const clickOutSuccessModal = () => {
    setSubmitSuccessful(false);
    history.push("/mortgage");
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.container}>
          <span className={styles.instruction}>
            Please fill the form below to begin your mortgage application
            process
          </span>

          <div className={styles.grid}>
            <Controller
              name="hasAccessAccount"
              control={control} 
              render={({ field }) => (
                <InputWithHeader
                  header={
                    isBusinessApplicant
                      ? "Do you use Access Bank for your Business Account?"
                      : "Do you have an Access Bank Account?"
                  }
                  placeholder="Select"
                  type="dropdown"
                  value={
                    field.value === true
                      ? "Yes"
                      : field.value === false
                      ? "No"
                      : ""
                  }
                  onPressDropDown={() =>
                    toggleDropdown("hasAccessAccount", true)
                  }
                  error={errors.hasAccessAccount?.message}
                />
              )}
            />

            {hasAccessAccount ? (
              <Controller
                name="accountNumber"
                control={control}
                render={({ field }) => (
                  <InputWithHeader
                    header={
                      isBusinessApplicant
                        ? "Enter your Access Bank Business Account Number"
                        : "Access Bank Account Number"
                    }
                    placeholder="Enter Account Number"
                    type="string"
                    value={field.value || ""}
                    formKey="accountNumber"
                    handleChange={(_, val) => field.onChange(val)}
                    error={errors.accountNumber?.message}
                  />
                )}
              />
            ) : null}

            {isBusinessApplicant ? (
              <Controller
                name="businessRegistration"
                control={control}
                render={({ field }) => (
                  <FileUploadInput
                    header="Business Registration Document"
                    placeholder="Upload business registration document"
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.businessRegistration}
                  />
                )}
              />
            ) : (
              <>
                <Controller
                  name="letterOfEmployment"
                  control={control}
                  render={({ field }) => (
                    <FileUploadInput
                      header="Letter of Employment"
                      placeholder="Upload letter of employment"
                      value={field.value}
                      onChange={field.onChange}
                      error={errors.letterOfEmployment}
                    />
                  )}
                />

                <Controller
                  name="paySlip"
                  control={control}
                  render={({ field }) => (
                    <FileUploadInput
                      header="Last 6 months Payslip"
                      placeholder="Upload 6 months payslip"
                      value={field.value}
                      onChange={field.onChange}
                      error={errors.paySlip}
                    />
                  )}
                />

                <Controller
                  name="evidenceOfPayment"
                  control={control}
                  render={({ field }) => (
                    <FileUploadInput
                      header="Evidence of Additional Payment for the Past 12 Months"
                      placeholder="Upload evidence of payment"
                      value={field.value}
                      onChange={field.onChange}
                      error={errors.evidenceOfPayment}
                    />
                  )}
                />
              </>
            )}

            <Controller
              name="statementOfAccount"
              control={control}
              render={({ field }) => (
                <FileUploadInput
                  header="Statement of Bank Account for the Last 12 Months"
                  placeholder="Upload Bank Account Statement"
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.statementOfAccount}
                />
              )}
            />

            <Controller
              name="meansOfIdentification"
              control={control}
              render={({ field }) => (
                <FileUploadInput
                  header="Means of Identification (NIN, Voter’s Card or International Passport)"
                  placeholder="Upload Means of Identification"
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.meansOfIdentification}
                />
              )}
            />
          </div>

          <div className={styles["bottom-cont"]}>
            <div className={styles["back-but"]} role="button">
              <span>Back</span>
            </div>
            <button type="submit" className={styles["submit-btn"]}>
              Submit Application
            </button>
          </div>
        </div>
      </form>

      <ModalLayout
        modalOpen={dropDownStates.hasAccessAccount}
        onClickOut={() => toggleDropdown("hasAccessAccount", false)}
      >
        <PopUp
          options={["Yes", "No"]}
          click={(name) => {
            setValue("hasAccessAccount", name === "Yes", {
              shouldValidate: true,
            });
            toggleDropdown("hasAccessAccount", false);
          }}
        />
      </ModalLayout>

      <ModalLayout
        modalOpen={submitSuccessful}
        onClickOut={() => clickOutSuccessModal()}
      >
        <SuccessModal
          onClose={() => clickOutSuccessModal()}
          message="Your application has been submitted. We'll be in touch shortly."
        />
      </ModalLayout>
    </>
  );
};

export default IntMortgageApp;
